import React, { useState } from 'react';
import { Button, Card, FloatingLabel, Form, Toast, ToastContainer } from 'react-bootstrap';
import InputMask from "react-input-mask";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../providers';
import { PaymentOptionsEnum } from '../../../business';
import { PaymentStrings, RequestAdsPaymentData, RequestPaymentData, SubscriptionPlanChosen, SubscriptionType } from '../../../business/core';
import { PaymentControllerImpl } from '../../../business/impl';

const CPF_MASK = "999.999.999-99";
const CNPJ_MASK = "99.999.999/9999-99";

export const FormBankSlip: React.FC<SubscriptionPlanChosen> = ({ id, frequency, isToUpdate, subscriptionType, isFree }) => {
    const { userData, handleLogin } = useAuth();
    const navigate = useNavigate();
    const { inputMask, identification_number_label } = PaymentStrings['PTBR'];
    const strings = PaymentStrings['PTBR'].payment_options.bank_slip.form;

    const [identificationNumber, setIdentificationNumber] = useState<string>("");
    const [identificationMask, setIdentificationMask] = useState<string>(CPF_MASK);
    const [identificationNumberError, setIdentificationNumberError] = useState<boolean>(false);
    const [identificationNumberFocused, setIdentificationNumberFocused] = useState<boolean>(false);
    const [phone, setPhone] = useState<string>(userData.phone);
    const [email, setEmail] = useState<string>(userData.email);
    const [emailError, setEmailError] = useState<boolean>(false);
    const [phoneError, setPhoneError] = useState<boolean>(false);
    const [validated, setValidated] = useState(false);
    const [toastingError, setToastingError] = useState<string | null>(null);
    const [toastingSuccess, setToastingSuccess] = useState<string | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const paymentMethod = PaymentOptionsEnum.BANK_SLIP;

    const controller = new PaymentControllerImpl();

    const handleChange = (e) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        setIdentificationNumber(rawValue);
    };

    const handleMaskChange = () => {
        if (identificationNumber.length === 11) {
            setIdentificationMask(CPF_MASK);
        } else if (identificationNumber.length > 11) {
            setIdentificationMask(CNPJ_MASK);
        }
        setIdentificationNumberFocused(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setProcessing(true);

        let requestData: RequestAdsPaymentData | RequestPaymentData;
        switch (subscriptionType) {
            case SubscriptionType.ADS:
                requestData = {
                    ads_id: id,
                    method: paymentMethod,
                    email: email,
                    identification: identificationNumber
                };

                break;

            case SubscriptionType.SUBSCRIPTION:
                requestData = {
                    plan_id: id,
                    frequency: frequency,
                    method: paymentMethod,
                    email: email,
                    identification: identificationNumber
                };
                break;

            default:
                requestData = {
                    ads_id: '',
                    method: '',
                    email: '',
                    identification: ''
                };
                break;
        }

        setValidated(true);
        try {
            const response = await controller.requestPayment(requestData, isToUpdate);
            if (response.token) {
                handleLogin(response.token);
            }
            setToastingSuccess(response.response);
            setTimeout(() => { isToUpdate ? window.location.reload() : navigate('/adm/dashboard') }, 3000);
        } catch (error) {
            setProcessing(false);
            setToastingError(error.message);
            if (error.message.includes('mail')) setEmailError(true);
            if (error.message.includes('phone')) setPhoneError(true);
            if (error.message.includes('identification')) setIdentificationNumberError(true);
            setValidated(false);
        }
        window.scrollTo(0, 0);
    };

    return (
        <>
            <ToastContainer
                className="p-3"
                position={'top-end'}
            >
                <Toast
                    bg={"danger"}
                    onClose={() => setToastingError(null)}
                    show={!!toastingError}
                    delay={5000}
                    autohide
                >
                    <Toast.Body className={"text-white"}>
                        {toastingError}
                    </Toast.Body>
                </Toast>
                <Toast
                    bg={"success"}
                    onClose={() => setToastingSuccess(null)}
                    show={!!toastingSuccess}
                    delay={5000}
                    autohide
                >
                    <Toast.Body className={"text-white"}>
                        {toastingSuccess}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
            {!isFree &&
                <Card.Title>{strings.title}</Card.Title>
            }
            <Card.Text>
                <Form validated={validated} onSubmit={handleSubmit}>
                    <FloatingLabel controlId="identificationNumber" label={strings.identification_number_label} className={"mb-5"}>
                        {identificationNumberFocused ? (
                            <Form.Control
                                type="tel"
                                required
                                minLength={11}
                                className={`custom-input ${identificationNumberError ? "input-error" : ""}`}
                                placeholder={identification_number_label}
                                isInvalid={identificationNumberError}
                                value={identificationNumber}
                                onChange={handleChange}
                                onBlur={handleMaskChange}
                                onFocus={() => setIdentificationNumberFocused(true)}
                                autoFocus
                            />
                        ) : (
                            <InputMask
                                mask={identificationMask}
                                value={identificationNumber}
                                onChange={handleChange}
                                onFocus={() => setIdentificationNumberFocused(true)}
                            >
                                {(inputProps) => (
                                    <Form.Control
                                        {...inputProps}
                                        type="tel"
                                        required
                                        minLength={14}
                                        className={`custom-input ${identificationNumberError ? "input-error" : ""}`}
                                        placeholder={identification_number_label}
                                        isInvalid={identificationNumberError}
                                    />
                                )}
                            </InputMask>
                        )}
                    </FloatingLabel>
                    <FloatingLabel
                        controlId="floatingInput"
                        label={strings.email_label}
                        className="mb-3"
                    >
                        <Form.Control
                            type="email"
                            placeholder="email@email.com"
                            isInvalid={emailError}
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                            required
                        />
                    </FloatingLabel>
                    <FloatingLabel controlId="phoneNumber" label={strings.phone_label} className={"mb-5"}>
                        <InputMask
                            mask={inputMask}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled
                        >
                            {(inputProps) => (
                                <Form.Control
                                    {...inputProps}
                                    type="tel"
                                    required
                                    minLength={20}
                                    className={`custom-input ${phoneError ? "input-error" : ""}`}
                                    placeholder={strings.phone_label}
                                    isInvalid={phoneError}
                                    disabled
                                />
                            )}
                        </InputMask>
                    </FloatingLabel>
                    <Button variant="primary" type={"submit"} disabled={processing}>{strings.button}</Button>
                </Form>
            </Card.Text>
        </>
    );
};
